// components/global-overlays.js
// ═══════════════════════════════════════════════════════════
// All global overlays: Search, QuickCapture, SpaceDisclaimer, InviteModal.
// Owns: useSearch hook, overlay visibility state.
// Receives: useSpace results as single object (spaceHook).
// Shell passes minimal triggers — overlays manage themselves.
// ═══════════════════════════════════════════════════════════
import { useState } from 'react';
import { useSearch } from '../hooks/use-search';
import { useNotes } from '../hooks/use-notes';
import SearchPanel from '../features/search-panel';
import QuickCapture from './quick-capture';
import SpaceDisclaimer from '../features/space-disclaimer';
import InviteModal from './invite-modal';

export default function GlobalOverlays({
  session, supabase, userRecord,
  threadHistory, onLoadThread,
  spaceHook,
  showSearch, setShowSearch,
  showInviteModal, setShowInviteModal,
  showCapture, setShowCapture,
}) {
  const { runSearch, searchQuery, setSearchQuery, searchResults, setSearchResults, searchLoading } = useSearch({ session, supabase });
  const { createNote } = useNotes({ userId: session?.user?.id, supabase });

  // destination comes from QuickCapture as 'ideas' | 'notes' (plural).
  // Map to the notes table's note_type enum (singular). Quick Capture
  // has no in-session context by design, so session_id + message_id
  // stay null — the user can still find it in the Notes grid.
  async function handleQuickCapture({ content, destination }) {
    setShowCapture(false);
    const type = destination === 'ideas' ? 'idea' : 'note';
    try {
      await createNote({ note_type: type, content });
    } catch (e) { console.error('QuickCapture save:', e); }
  }

  return (
    <>
      <SearchPanel
        showSearch={showSearch}
        setShowSearch={setShowSearch}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchResults={searchResults}
        setSearchResults={setSearchResults}
        searchLoading={searchLoading}
        runSearch={runSearch}
        threadHistory={threadHistory}
        userRecord={userRecord}
        session={session}
        isMobile={false}
        loadThread={(id) => {
          setShowSearch(false);
          onLoadThread?.(id);
        }}
      />

      <QuickCapture
        controlled={true}
        open={showCapture}
        onClose={() => setShowCapture(false)}
        onSave={handleQuickCapture}
      />

      {spaceHook.showSpaceDisclaimer && (
        <SpaceDisclaimer
          showSpaceDisclaimer={spaceHook.showSpaceDisclaimer}
          acceptSpaceDisclaimer={spaceHook.acceptSpaceDisclaimer}
          session={session}
          isMobile={false}
        />
      )}

      {showInviteModal && (
        <InviteModal
          showInviteModal={showInviteModal}
          setShowInviteModal={setShowInviteModal}
          sendSharedInvite={spaceHook.sendSharedInvite}
          inviteTab={spaceHook.inviteTab}
          setInviteTab={spaceHook.setInviteTab}
          inviteEmail={spaceHook.inviteEmail}
          setInviteEmail={spaceHook.setInviteEmail}
          inviteSpaceType={spaceHook.inviteSpaceType}
          setInviteSpaceType={spaceHook.setInviteSpaceType}
          inviteShiftMode={spaceHook.inviteShiftMode}
          setInviteShiftMode={spaceHook.setInviteShiftMode}
          inviteSpaceTitle={spaceHook.inviteSpaceTitle}
          setInviteSpaceTitle={spaceHook.setInviteSpaceTitle}
          inviteSpaceComment={spaceHook.inviteSpaceComment}
          setInviteSpaceComment={spaceHook.setInviteSpaceComment}
          inviteSpaceArticle={spaceHook.inviteSpaceArticle}
          setInviteSpaceArticle={spaceHook.setInviteSpaceArticle}
          inviteSpaceFile={spaceHook.inviteSpaceFile}
          setInviteSpaceFile={spaceHook.setInviteSpaceFile}
          inviteSpaceFileError={spaceHook.inviteSpaceFileError}
          inviteSpaceProfRole={spaceHook.inviteSpaceProfRole}
          setInviteSpaceProfRole={spaceHook.setInviteSpaceProfRole}
          inviteConsentChecked={spaceHook.inviteConsentChecked}
          setInviteConsentChecked={spaceHook.setInviteConsentChecked}
          inviteSending={spaceHook.inviteSending}
          inviteError={spaceHook.inviteError}
          setInviteError={spaceHook.setInviteError}
          inviteSuccess={spaceHook.inviteSuccess}
          inviteToShiftEmail={spaceHook.inviteToShiftEmail}
          setInviteToShiftEmail={spaceHook.setInviteToShiftEmail}
          inviteToShiftComment={spaceHook.inviteToShiftComment}
          setInviteToShiftComment={spaceHook.setInviteToShiftComment}
          inviteToShiftStatus={spaceHook.inviteToShiftStatus}
          setInviteToShiftStatus={spaceHook.setInviteToShiftStatus}
          inviteToShiftSending={spaceHook.inviteToShiftSending}
          inviteToShiftUrl={spaceHook.inviteToShiftUrl}
          handleInviteToShift={() => {}}
          handleInviteSpaceFileUpload={() => {}}
          messages={[]}
          send={() => {}}
          session={session}
          supabase={supabase}
          isMobile={false}
        />
      )}
    </>
  );
}